<template>
  <div
    @click="$router.push(`/tender-search/${tender.id}`)"
    class="tender-card cursor-pointer"
  >
    <!-- Top row: status + score -->
    <div class="flex items-center justify-between mb-3">
      <!-- The deadline badge, and beside it the portal's own list when it says
           something the deadline does not: an awarded contract has no closing
           date, so the deadline badge alone leaves it looking open. -->
      <div class="flex items-center gap-1.5 min-w-0">
        <StatusBadge :type="status.type" :label="status.label" />
        <span v-if="showsType" class="type-tag">
          {{ listingTypeLabel(tender.listing_type) }}
        </span>
      </div>
      <span v-if="tender.score" class="score-badge" :class="scoreClass">
        {{ tender.score }}
      </span>
    </div>

    <!-- Title — a real link so the card is reachable by keyboard; the card
         click stays as the larger mouse target. -->
    <h3 class="tender-card-title">
      <router-link :to="`/tender-search/${tender.id}`" @click.stop class="tender-card-link">
        <HighlightText :text="tender.title || 'Untitled tender'" :query="highlight" />
      </router-link>
    </h3>

    <!-- Org -->
    <p class="tender-card-org">
      <HighlightText :text="tender.organisation_name || '—'" :query="highlight" />
    </p>

    <!-- Tender ID — what people search and quote -->
    <p v-if="tender.tender_reference" class="tender-card-id" :title="tender.tender_reference">
      <HighlightText :text="tenderIdOf(tender.tender_reference)" :query="highlight" />
    </p>

    <!-- Bottom row: portal · date · amount -->
    <div class="tender-card-meta">
      <span class="meta-portal">{{ tender.portal_code }}</span>
      <span class="meta-dot">·</span>
      <span class="meta-date">{{ formatDate(tender.closing_date) }}</span>
      <span
        v-if="countdown"
        class="meta-countdown"
        :class="{ 'meta-countdown--urgent': countdown.urgent }"
      >
        <ClockIcon v-if="countdown.urgent" class="w-3.5 h-3.5" aria-hidden="true" />
        {{ countdown.text }}
      </span>
      <span v-if="tender.estimated_value" class="meta-dot">·</span>
      <span v-if="tender.estimated_value" class="meta-amount">{{ tender.estimated_value }}</span>

      <!-- Bookmark -->
      <button
        @click.stop="onBookmark"
        class="bookmark-btn"
        :aria-label="bookmarked ? 'Remove bookmark' : 'Bookmark'"
        :aria-pressed="bookmarked"
      >
        <StarSolidIcon v-if="bookmarked" class="w-4 h-4 text-amber-500" aria-hidden="true" />
        <StarIcon v-else class="w-4 h-4 text-slate-500 hover:text-amber-500 transition-colors" aria-hidden="true" />
      </button>
    </div>

    <!-- Project status row (Bookmarks page) -->
    <div v-if="showProjectStatus" class="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
      <!-- Bid state, not the tender's status (the badge above). -->
      <span
        class="text-xs font-semibold px-2.5 py-1 rounded-md"
        :class="tender.project_status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'"
      >
        {{ tender.project_status === 'active' ? 'Preparing' : 'Bookmarked' }}
      </span>
      <!-- Go opens the upload page for this tender. Viewers can't upload, so they don't get it. -->
      <button
        v-if="!viewOnly"
        type="button"
        @click.stop="onPrepareBid"
        :aria-label="`Go: upload PDFs for ${tender.title || 'this tender'}`"
        class="inline-flex items-center gap-1 bg-[#0071C5] hover:bg-[#005a9e] text-white text-xs font-bold px-3 py-1.5 rounded-[10px] transition-colors"
      >
        Go
      </button>
    </div>

    <LoginPromptModal
      :show="showLoginPrompt"
      message="Please login to bookmark tenders."
      @close="showLoginPrompt = false"
    />
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";
import { StarIcon, ClockIcon } from "@heroicons/vue/24/outline";
import { StarIcon as StarSolidIcon } from "@heroicons/vue/24/solid";

import StatusBadge from "./StatusBadge.vue";
import { listingTypeLabel, isDecided } from "../utils/listingType";
import HighlightText from "./HighlightText.vue";
import LoginPromptModal from "./LoginPromptModal.vue";
import { useBookmarks } from "../store/bookmarks";
import { formatDate, deadlineCountdown, getTenderStatus } from "../utils/dateUtils";
import { tenderIdOf } from "../utils/tenderId";
import { isLoggedIn, isViewOnly } from "../utils/auth";

const props = defineProps({
  tender: { type: Object, required: true },
  showProjectStatus: { type: Boolean, default: false },
  // The applied search term; matches are marked in title, organisation and ID.
  highlight: { type: String, default: "" },
});

const { isBookmarked, toggle, updateStatus } = useBookmarks();
const toast = useToast();
const router = useRouter();
const showLoginPrompt = ref(false);
const viewOnly = isViewOnly();

const bookmarked = computed(() => isBookmarked(props.tender.id));
const status     = computed(() => getTenderStatus(props.tender.closing_date));

// Only the types that say something the deadline badge does not. "Live" and
// "Active" repeat it, so showing them would be noise on every card.
const showsType  = computed(() =>
  !!props.tender.listing_type && isDecided(props.tender.listing_type)
);
const countdown  = computed(() => deadlineCountdown(props.tender.closing_date));

const scoreClass = computed(() => {
  const s = props.tender.score;
  if (!s) return '';
  if (s >= 80) return 'score-high';
  if (s >= 60) return 'score-mid';
  return 'score-low';
});

// Marks the bookmark as being prepared (it shows under Bids › To Upload),
// then opens the upload page with the tender attached.
async function onPrepareBid() {
  if (!isLoggedIn()) { showLoginPrompt.value = true; return; }
  const tenderId = props.tender.tender_id ?? props.tender.id;
  if (props.tender.project_status !== 'active') {
    try {
      await updateStatus(tenderId, 'active');
    } catch {
      toast.error("Couldn't start preparing this bid. Check your connection and try again.");
      return;
    }
  }
  router.push({
    name: 'Home',
    query: { tender_id: tenderId, tender_name: props.tender.title || '' },
  });
}

async function onBookmark() {
  if (!isLoggedIn()) { showLoginPrompt.value = true; return; }
  const wasBookmarked = bookmarked.value;
  try {
    await toggle(props.tender);
    toast.success(wasBookmarked ? "Removed from bookmarks" : "Bookmarked");
  } catch {
    toast.error("Couldn't update the bookmark. Check your connection and try again.");
  }
}
</script>

<style scoped>
.tender-card {
  background: #ffffff;
  border: 1px solid #e8ecf0;
  border-radius: 12px;
  padding: 1.1rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  transition: box-shadow 0.18s, border-color 0.18s;
}

.tender-card:hover {
  box-shadow: 0 4px 20px rgba(15, 23, 42, 0.10);
  border-color: #c8d3df;
}

.tender-card-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #0f172a;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.35rem;
}

.tender-card:hover .tender-card-title,
.tender-card-link:focus-visible {
  color: #0071C5;
}

.tender-card-link {
  color: inherit;
}

.tender-card-org {
  font-size: 0.78rem;
  color: #64748b;
  margin-bottom: 0.25rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tender-card-id {
  font-family: var(--font-mono, "JetBrains Mono", monospace);
  font-size: 0.72rem;
  color: #475569;
  margin-bottom: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tender-card-meta {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.75rem;
  color: #64748b;
  border-top: 1px solid #f1f5f9;
  padding-top: 0.7rem;
  margin-top: auto;
}

.meta-portal {
  font-weight: 700;
  font-size: 0.7rem;
  color: #475569;
  background: #f1f5f9;
  padding: 2px 7px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.meta-dot {
  color: #64748b;
}

.meta-date {
  color: #64748b;
}

.meta-countdown {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  color: #64748b;
  white-space: nowrap;
}

.meta-countdown::before {
  content: "·";
  color: #64748b;
  margin-right: 0.3rem;
}

/* Inside the 72-hour window: amber-700, 5.0:1 on white */
.meta-countdown--urgent {
  color: #b45309;
  font-weight: 600;
}

.meta-amount {
  color: #64748b;
}

/* 6px padding around the 16px star gives a 28px target. */
.bookmark-btn {
  background: none;
  border: none;
  padding: 6px;
  margin: -6px -6px -6px auto;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
}

/* The tender's list on its portal (Awarded, Under Evaluation, In Progress).
   Quieter than StatusBadge on purpose: the deadline is the primary signal and
   this qualifies it. #475569 on #f1f5f9 is 7.4:1. */
.type-tag {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 600;
    line-height: 1.2;
    color: #475569;
    background: #f1f5f9;
    border-radius: 4px;
    padding: 0.15rem 0.4rem;
    white-space: nowrap;
}

/* Score badge */
.score-badge {
  font-size: 0.85rem;
  font-weight: 700;
  min-width: 32px;
  text-align: right;
}

.score-high { color: #16a34a; }
.score-mid  { color: #d97706; }
.score-low  { color: #dc2626; }
</style>
